/* Adding a joiner: the plan, and the order it is carried out in.
 *
 * The "Add a joiner" flow collects decisions across five screens and writes NOTHING until the
 * last one. This is the part that then turns those decisions into writes. It is pure (the writes
 * themselves are handed in), so the order, the payloads and what a retry skips can all be tested
 * without a network.
 *
 * WHY ORDER AND RETRY MATTER
 *
 * Hand to HR changes Entra groups, a device register and a SharePoint folder, then opens a case in
 * another app. Any one of those can fail on its own, and the person pressing the button must be
 * told which, and be able to press it again WITHOUT repeating what already worked. Group writes
 * are idempotent, but a device assignment is not: assigning a laptop somebody already holds opens
 * a second spell in its history. So every step records its own result, and a retry skips what is
 * done.
 */

import { APPS, roleFor, type AppRole } from '@hird/access';
import type { IDevice, IJoinerDraft, IPerson, IRoleTemplate } from '../models';
import { isRealPerson } from './registers';

export type JoinerStepId = 'details' | 'access' | 'equipment' | 'folder' | 'case';

export interface JoinerStep {
  id: JoinerStepId;
  label: string;
  /** What this step will do, in words, for the review screen. */
  detail: string;
}

export type StepState = 'done' | 'failed' | 'skipped';

export interface StepResult {
  id: JoinerStepId;
  state: StepState;
  message: string;
}

/** Where clicking a row in the Onboarding list goes.
 *
 *  A NEW starter (in the "To set up" view) goes to the Add a joiner flow, so there is one way in and IT
 *  cannot finish somebody from the old side panel and skip HR, the forms and Accounts. Everything else
 *  keeps the side panel, which is still how a record is edited, a leaver wound down, or somebody
 *  finished who was part-way through the old way. An account nobody has classified yet (a shared
 *  mailbox, a service account) also stays on the panel, because classifying it is done there and it is
 *  not a person to hand to HR. And with no Onboarding app configured there is nowhere to hand over to. */
export function rowTarget(person: Pick<IPerson, 'kind'>, lens: string, joinersOn: boolean): 'wizard' | 'drawer' {
  if (!joinersOn || lens !== 'starters') return 'drawer';
  return isRealPerson(person) ? 'wizard' : 'drawer';
}

/** Whether text is one usable email address. The welcome goes to exactly one person, so a list is not. */
export function isEmailAddress(text: string): boolean {
  return /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(text.trim());
}

/** A blank draft for somebody who is being added, taking what the person's row already knows. */
export function blankDraft(person: Pick<IPerson, 'title' | 'loc' | 'dept' | 'start'>): IJoinerDraft {
  return {
    templateId: '',
    title: person.title ?? '',
    loc: person.loc ?? '',
    dept: person.dept ?? '',
    start: person.start ?? '',
    apps: {},
    deviceIds: [],
    welcomeTo: '',
    level: 1,
  };
}

/** Applying a template: its details and its access become the draft, and anything already typed
 *  is kept where the template has nothing to say, so choosing a template never wipes a field. */
export function applyTemplate(draft: IJoinerDraft, template: IRoleTemplate | null): IJoinerDraft {
  if (!template) return { ...draft, templateId: '', apps: {} };
  return {
    ...draft,
    templateId: template.id,
    title: template.jobTitle || draft.title,
    loc: template.depot || draft.loc,
    dept: template.department || draft.dept,
    apps: { ...template.apps },
  };
}

/** What a Hub level gives this app on its own. An app whose wanted tier already matches what the level
 *  gives needs no group of its own. Level 2 (an administrator) is the top tier of nearly everything. */
export function levelRole(appId: string, level: 1 | 2): AppRole {
  const app = APPS.find((a) => a.appId === appId);
  return app ? roleFor(app, level) : 'none';
}

/** What Level 1 gives this app on its own, which is what almost every joiner gets. */
export const levelOneRole = (appId: string): AppRole => levelRole(appId, 1);

/** The level a draft will be given: 1 unless somebody chose 2. */
export const levelOf = (draft: Pick<IJoinerDraft, 'level'>): 1 | 2 => (draft.level === 2 ? 2 : 1);

export interface AccessChange {
  appId: string;
  appName: string;
  role: AppRole;
}

/** The apps that need a group written: those whose wanted tier is not what Level 1 already gives.
 *  Anything the draft leaves unset follows Level 1 and is not written at all. */
export function accessChanges(apps: Record<string, AppRole>, level: 1 | 2 = 1): AccessChange[] {
  const out: AccessChange[] = [];
  for (const app of APPS) {
    const want = apps[app.appId];
    if (want === undefined) continue;
    if (want === levelRole(app.appId, level)) continue;
    out.push({ appId: app.appId, appName: app.appName, role: want });
  }
  return out;
}

/** Things that must be true before Hand to HR can be pressed. Each is a sentence, because the
 *  review screen shows them as they are. */
export function joinerProblems(
  draft: IJoinerDraft,
  person: Pick<IPerson, 'graphId' | 'upn'>,
): string[] {
  const out: string[] = [];
  if (!person.graphId) out.push('The sync has not matched this account to Entra yet, so access cannot be granted.');
  if (!person.upn) out.push('This account has no sign-in name.');
  if (!draft.loc.trim()) out.push('Choose a depot.');
  if (!draft.start.trim()) out.push('Enter a start date.');
  if (!draft.title.trim()) out.push('Enter a job title.');
  /* The welcome email is sent at hand-over, so it has to have somewhere to go. */
  if (!isEmailAddress(draft.welcomeTo ?? '')) out.push('Give the address their welcome email goes to.');
  return out;
}

/** The steps, in the order they run, described for the review screen. */
export function planHandToHr(
  draft: IJoinerDraft,
  devices: readonly Pick<IDevice, 'id' | 'tag'>[],
): JoinerStep[] {
  const lvl = levelOf(draft);
  const changes = accessChanges(draft.apps, lvl);
  const kit = draft.deviceIds
    .map((id) => devices.find((d) => d.id === id)?.tag)
    .filter((t): t is string => !!t);
  return [
    {
      id: 'details',
      label: 'Save their details',
      detail: 'Recorded as a person at ' + (draft.loc || 'no depot') + ', starting ' + (draft.start || 'no date') + '.',
    },
    {
      id: 'access',
      label: 'Grant access',
      detail: changes.length === 0
        ? (lvl === 2 ? 'Hub Level 2 (administrator), which already includes the top tier of their apps.' : 'Hub Level 1, which already includes their standard apps.')
        : 'Hub Level ' + lvl + (lvl === 2 ? ' (administrator)' : '') + ', plus ' + changes.map((c) => c.appName).join(', ') + '.',
    },
    {
      id: 'equipment',
      label: 'Assign equipment',
      detail: kit.length === 0 ? 'Nothing to assign.' : 'Assign ' + kit.join(', ') + '.',
    },
    {
      id: 'folder',
      label: 'Create their folder',
      detail: draft.welcomeTo && draft.welcomeTo.trim()
        ? 'Their personal folder in the Hub, and the welcome email to ' + draft.welcomeTo.trim() + '.'
        : 'Their personal folder in the Hub, and the welcome email.',
    },
    {
      id: 'case',
      label: 'Open the case for HR',
      detail: 'Their case appears on the Onboarding board as ready for HR, and the HR team are emailed that somebody is ready to onboard.',
    },
  ];
}

/** The writes, handed in. Each throws to report failure; the message is what is shown. */
export interface JoinerOps {
  saveDetails(patch: Partial<IPerson>): Promise<void>;
  /** Sets the Hub level: 1 for staff, 2 for an administrator. */
  setLevel(level: 1 | 2): Promise<void>;
  setAppRole(appId: string, role: AppRole): Promise<void>;
  assign(deviceId: string): Promise<{ problems: string[] }>;
  createFolder(): Promise<{ message: string }>;
  /** Opens the case and tells HR. Answers in words (who was emailed, or why not); the step shows it. */
  openCase(): Promise<string | void>;
}

export interface RunInput {
  draft: IJoinerDraft;
  personId: string;
  devices: readonly Pick<IDevice, 'id' | 'user'>[];
  /** Steps already finished on an earlier attempt. */
  done?: ReadonlySet<JoinerStepId>;
  ops: JoinerOps;
  /** Told as each step starts and ends, so the screen can show where it is. */
  onStep?: (r: StepResult | { id: JoinerStepId; state: 'running'; message: string }) => void;
}

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** Runs the steps in order and stops at the first failure.
 *
 *  Stops, because the later steps depend on the earlier ones: a case for HR must not open for
 *  somebody whose access never got granted, and a folder is pointless if the details were wrong.
 *  Everything before the failure stays done, and `done` on the next call skips it. */
export async function runHandToHr(input: RunInput): Promise<StepResult[]> {
  const { draft, personId, devices, ops, onStep } = input;
  const done = new Set(input.done ?? []);
  const results: StepResult[] = [];

  const step = async (
    id: JoinerStepId,
    label: string,
    run: () => Promise<string>,
  ): Promise<boolean> => {
    if (done.has(id)) {
      const r: StepResult = { id, state: 'skipped', message: 'Already done.' };
      results.push(r);
      onStep?.(r);
      return true;
    }
    onStep?.({ id, state: 'running', message: label + '...' });
    try {
      const msg = await run();
      const r: StepResult = { id, state: 'done', message: msg };
      results.push(r);
      onStep?.(r);
      return true;
    } catch (e) {
      const r: StepResult = { id, state: 'failed', message: message(e) };
      results.push(r);
      onStep?.(r);
      return false;
    }
  };

  const patch: Partial<IPerson> = {
    kind: 'User',
    loc: draft.loc,
    start: draft.start,
    title: draft.title,
    dept: draft.dept,
  };

  if (!(await step('details', 'Saving their details', async () => {
    await ops.saveDetails(patch);
    return 'Details saved.';
  }))) return results;

  if (!(await step('access', 'Granting access', async () => {
    const lvl = levelOf(draft);
    await ops.setLevel(lvl);
    const changes = accessChanges(draft.apps, lvl);
    for (const c of changes) await ops.setAppRole(c.appId, c.role);
    const word = 'Hub Level ' + lvl + (lvl === 2 ? ' (administrator)' : '');
    return changes.length === 0 ? word + ' granted.' : word + ' and ' + changes.length + ' app' + (changes.length === 1 ? '' : 's') + ' granted.';
  }))) return results;

  if (!(await step('equipment', 'Assigning equipment', async () => {
    const problems: string[] = [];
    let assigned = 0;
    for (const id of draft.deviceIds) {
      // Already theirs from an earlier attempt: assigning again would open a second spell.
      if (devices.find((d) => d.id === id)?.user === personId) continue;
      const r = await ops.assign(id);
      problems.push(...r.problems);
      assigned += 1;
    }
    if (problems.length > 0) throw new Error(problems.join(' '));
    return assigned === 0 ? 'Nothing to assign.' : assigned + ' device' + (assigned === 1 ? '' : 's') + ' assigned.';
  }))) return results;

  if (!(await step('folder', 'Creating their folder', async () => (await ops.createFolder()).message))) return results;

  await step('case', 'Opening the case', async () => {
    return (await ops.openCase()) || 'Ready for HR.';
  });
  return results;
}

/** True when every step finished (or was skipped because it already had). */
export function allDone(results: readonly StepResult[]): boolean {
  return results.length > 0 && results.every((r) => r.state !== 'failed') && results.some((r) => r.id === 'case');
}
